import React from 'react'
import PropTypes from 'prop-types'

import { AppLayout } from './appLayout/AppLayout'
import { Notifications } from '../common/notifications/Notifications'
import { useAuthStore } from '../store/auth'
import { useDependencies } from '../store/dependencies'
import { getDependencyCall } from '../lib/api/get'

import '../assets/css/custom.min.css'
import '../assets/css/icons.min.css'

/**
 * ModuleBuilder Component
 *
 * A React component that wraps the application layout and handles global functionalities like notifications, login modals, and environment configurations.
 *
 * @module ModuleBuilder
 * @description A React component that wraps the application layout and handles global functionalities like notifications, login modals, and environment configurations.
 * @example
 * return (
 *   <ModuleBuilder
 *     envData={envData}
 *     setToggleLoginModal={setToggleLoginModal}
 *     toggleLoginModal={toggleLoginModal}
 *     sideLoginModalRef={sideLoginModalRef}
 *     isLocalEnvironment={isLocalEnvironment}
 *     authDetails={authDetails}
 *     appDataParsed={appDataParsed}
 *     routeData={routeData}
 *   />
 * );
 *
 * @param {Object} envData - Environment-specific configuration data.
 * @param {string} isLocalEnvironment - A string indicating whether the environment is local.
 * @param {Object} authDetails - Authentication details, including login status.
 * @param {Object} appDataParsed - Parsed application data used for rendering.
 * @param {Object} routeData - Route-specific data, including page and component details.
 *
 * @returns {React.Element} A React component containing the application layout, notifications, and optionally a login modal.
 */
export const ModuleBuilder = ({
  envData,
  isLocalEnvironment,
  authDetails,
  appDataParsed,
  routeData,
  allData,
  setActiveOrgRoutes,
  activeOrgRoutes,
  isDefaultOrg,
  setIsDefaultOrg,
}) => {
  const urlPath = window.location.pathname
  const urlLink = urlPath.split('/organization/')

  if (activeOrgRoutes !== null && urlLink.length && urlLink.length > 1) {
    const orgPageAccess = activeOrgRoutes.formData?.Pages?.filter(
      (f) => f.pageName === routeData.pageName,
    )
    if (orgPageAccess.length > 0) {
      const orgPageRoles = orgPageAccess[0].pageRoles.filter(
        (f) => f.roleName === authDetails.orgRole,
      )
      if (orgPageRoles.length <= 0) {
        window.location.href = '/console/dashboard'
      }
    }
  } else {
    const pageAccess = authDetails.session.pages.filter((f) => f.pageName === routeData.pageName)
    if (pageAccess.length > 0) {
      const pageRoles = pageAccess[0].pageRoles.filter((f) => f.roleName === authDetails.role)
      if (pageRoles.length <= 0) {
        window.location.href = '/console/dashboard'
      }
    }
  }
  const appState = useAuthStore((state) => state)
  const { setDependency } = useDependencies()
  const [gettingDeps, setGettingDeps] = React.useState(false)

  const getDeps = () => {
    const stateVar = { ...appState }
    if (JSON.parse(routeData.apiEndPointSchema).hasDependencies) {
      JSON.parse(routeData.apiEndPointSchema).dependeciesFormAPIS.map(async (api) => {
        setGettingDeps(true)
        if (stateVar.dependeciesFormAPIS) {
          stateVar.dependeciesFormAPIS[api] = await getDependencyCall(
            isLocalEnvironment,
            api,
            '&isDependency=true',
          )
        } else {
          stateVar['dependeciesFormAPIS'] = {}
          stateVar.dependeciesFormAPIS[api] = await getDependencyCall(
            isLocalEnvironment,
            api,
            '&isDependency=true',
          )
        }
        setDependency(stateVar, stateVar.role, stateVar.roleId)
        setGettingDeps(false)
      })
    }
  }

  React.useEffect(() => {
    getDeps()
  }, [])

  return (
    <div id="layout-wrapper">
      <Notifications />
      <AppLayout
        envData={envData}
        allData={allData}
        isLocalEnvironment={isLocalEnvironment}
        authDetails={authDetails}
        routeData={routeData}
        appDataParsed={appDataParsed}
        gettingDeps={gettingDeps}
        setActiveOrgRoutes={setActiveOrgRoutes}
        activeOrgRoutes={activeOrgRoutes}
        isDefaultOrg={isDefaultOrg}
        setIsDefaultOrg={setIsDefaultOrg}
      />
      <div className="trtui-vertical-overlay" />
    </div>
  )
}

ModuleBuilder.propTypes = {
  envData: PropTypes.object.isRequired,
  isLocalEnvironment: PropTypes.string.isRequired,
  authDetails: PropTypes.object.isRequired,
  appDataParsed: PropTypes.object.isRequired,
  routeData: PropTypes.object.isRequired,
  allData: PropTypes.object,
  setActiveOrgRoutes: PropTypes.func,
  activeOrgRoutes: PropTypes.object,
  isDefaultOrg: PropTypes.object,
  setIsDefaultOrg: PropTypes.func,
}
